import { CART_API_URL } from "../helpers/Constant";
import { authService } from "./authService";

export const cartService = {
  // Get cart details
  getCart: async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        console.log("🔑 No auth token found, skipping cart fetch");
        return null;
      }

      console.log("📡 Fetching cart from:", CART_API_URL);

      const response = await fetch(CART_API_URL, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      console.log("📡 GET Cart Response Status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ GET Cart Error Response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const result = await response.json();
      console.log("📦 Full GET Cart Response:", result);
      
      // Check different possible response structures
      if (result?.data?.status === 200 && result.data.data) {
        console.log("✅ Cart data found:", result.data.data);
        return result.data.data;
      } else if (result?.status === 200 && result.data) {
        console.log("✅ Cart data found (alternate structure):", result.data);
        return result.data;
      } else if (result?.success && result.data) {
        console.log("✅ Cart data found (alternate structure 2):", result.data);
        return result.data;
      }
      
      console.log("⚠️ Cart is empty or invalid response structure");
      return null;
    } catch (error) {
      console.error("❌ Error fetching cart:", error);
      throw error;
    }
  },

  // Add item to cart or update quantity - UPDATED to match API
  addOrUpdateCart: async (menuItemId, addressId, quantity, instructions = "") => {
    try {
      const token = authService.getToken();
      if (!token) {
        console.error("❌ No authentication token");
        throw new Error("No authentication token");
      }

      const payload = {
        menu_item_id: parseInt(menuItemId),  // Changed from menu_id to menu_item_id
        quantity: parseInt(quantity),
        instructions: instructions || "",
        address_id: parseInt(addressId)
      };

      console.log("📤 Sending payload to cart API:", JSON.stringify(payload, null, 2));
      console.log("📡 API URL:", CART_API_URL);
      console.log("🔑 Auth Token present:", !!token);

      const response = await fetch(CART_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("📡 POST Cart Response Status:", response.status);

      const responseText = await response.text();
      console.log("📥 Raw Response Text:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("📦 Parsed Response:", result);
      } catch (parseError) {
        console.error("❌ Failed to parse response as JSON:", parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      // Check for success with multiple possible response structures
      if (response.ok || result?.data?.status === 200 || result?.status === 200) {
        console.log("✅ Cart update successful");
        return { 
          success: true, 
          data: result.data,
          message: result.data?.message || result.message || "Cart updated successfully"
        };
      } 
      
      // Check for conflict (different restaurant)
      if (result?.data?.status === 409 || result?.status === 409 || response.status === 409) {
        console.log("⚠️ Cart conflict - different restaurant");
        return { 
          success: false, 
          error: "CART_CONFLICT",
          message: result?.data?.message || result?.message || "Cart contains items from another restaurant"
        };
      }
      
      // Handle other error cases
      console.error("❌ Cart update failed:", result);
      return { 
        success: false, 
        error: result?.data?.message || result?.message || "Failed to update cart",
        details: result
      };

    } catch (error) {
      console.error("❌ Error in cart operation:", error);
      
      // Check if it's a network error
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        return {
          success: false,
          error: "Network error. Please check your internet connection."
        };
      }
      
      throw error;
    }
  },

  // Remove item from cart (set quantity to 0)
  removeFromCart: async (menuItemId, addressId) => {
    try {
      // Use the same addOrUpdateCart with quantity 0
      return await this.addOrUpdateCart(menuItemId, addressId, 0);
    } catch (error) {
      console.error("❌ Error removing item from cart:", error);
      throw error;
    }
  },

  // Update item quantity
  updateQuantity: async (menuItemId, addressId, quantity, instructions = "") => {
    try {
      return await this.addOrUpdateCart(menuItemId, addressId, quantity, instructions);
    } catch (error) {
      console.error("❌ Error updating quantity:", error);
      throw error;
    }
  },

  // Clear entire cart
  clearCart: async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        console.error("❌ No authentication token");
        throw new Error("No authentication token");
      }

      console.log("🗑️ Clearing cart at:", CART_API_URL);

      const response = await fetch(CART_API_URL, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      console.log("📡 DELETE Cart Response Status:", response.status);

      const responseText = await response.text();
      console.log("📥 Raw DELETE Response:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("📦 Parsed DELETE Response:", result);
      } catch (parseError) {
        console.error("❌ Failed to parse response as JSON:", parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (response.ok || result?.data?.status === 200 || result?.status === 200) {
        console.log("✅ Cart cleared successfully");
        return { 
          success: true,
          message: result?.data?.message || result?.message || "Cart cleared successfully"
        };
      } else {
        console.error("❌ Failed to clear cart:", result);
        return { 
          success: false, 
          error: result?.data?.message || result?.message || "Failed to clear cart",
          details: result
        };
      }
    } catch (error) {
      console.error("❌ Error clearing cart:", error);
      throw error;
    }
  },

  // Get cart count
  getCartCount: async () => {
    try {
      const cartData = await this.getCart();
      if (cartData?.cart?.items) {
        return cartData.cart.items.reduce((total, item) => total + (item.quantity || 0), 0);
      }
      return 0;
    } catch (error) {
      console.error("❌ Error getting cart count:", error);
      return 0;
    }
  },

  // Get cart total amount
  getCartTotal: async () => {
    try {
      const cartData = await this.getCart();
      if (cartData?.cart?.items) {
        return cartData.cart.items.reduce((total, item) => total + (parseFloat(item.total_price) || 0), 0);
      }
      return 0;
    } catch (error) {
      console.error("❌ Error getting cart total:", error);
      return 0;
    }
  },

  // Check if cart has items from different restaurant
  checkRestaurantConflict: async (restaurantId) => {
    try {
      const cartData = await this.getCart();
      if (cartData?.cart?.restaurant_id && 
          cartData.cart.restaurant_id !== restaurantId && 
          cartData.cart.items?.length > 0) {
        return {
          hasConflict: true,
          currentRestaurantId: cartData.cart.restaurant_id,
          currentRestaurantName: cartData.cart.restaurant_name || "Another restaurant"
        };
      }
      return { hasConflict: false };
    } catch (error) {
      console.error("❌ Error checking restaurant conflict:", error);
      return { hasConflict: false };
    }
  },

  // Validate cart payload
  validatePayload: (payload) => {
    const errors = [];
    
    if (!payload.menu_item_id || isNaN(payload.menu_item_id)) {
      errors.push("Invalid menu_item_id");
    }
    
    if (payload.quantity === undefined || payload.quantity === null || isNaN(payload.quantity)) {
      errors.push("Invalid quantity");
    }
    
    if (!payload.address_id || isNaN(payload.address_id)) {
      errors.push("Invalid address_id");
    }
    
    if (payload.quantity < 0) {
      errors.push("Quantity cannot be negative");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Safe add to cart with validation
  safeAddOrUpdateCart: async (menuItemId, addressId, quantity, instructions = "") => {
    const payload = {
      menu_item_id: parseInt(menuItemId),
      quantity: parseInt(quantity),
      instructions: instructions || "",
      address_id: parseInt(addressId)
    };

    const validation = this.validatePayload(payload);
    if (!validation.isValid) {
      console.error("❌ Invalid payload:", validation.errors);
      return {
        success: false,
        error: `Invalid payload: ${validation.errors.join(", ")}`
      };
    }

    return await this.addOrUpdateCart(menuItemId, addressId, quantity, instructions);
  }
};